import React from 'react';
import {
  Button,
  Group,
  Modal,
  TextInput,
  Text,
  Stack,
  ScrollArea,
  SimpleGrid,
  UnstyledButton,
  Flex,
  Box,
} from '@mantine/core';
import { IconX, IconPlus } from '@tabler/icons-react';
import { primaryColor } from '../../constants/theme';

type SearchTermsModalProps = {
  opened: boolean;
  searchTerm: string;
  searchTerms: string[];
  onSearchTermChange: (value: string) => void;
  onAddTerm: () => void;
  onDeleteTerm: (term: string) => void;
  onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onCancel: () => void;
};

export const SearchTermsModal: React.FC<SearchTermsModalProps> = ({
  opened,
  searchTerm,
  searchTerms,
  onSearchTermChange,
  onAddTerm,
  onDeleteTerm,
  onKeyPress,
  onSave,
  onCancel,
}) => {
  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={<Text fw={600} size="xl">Edit Search Terms</Text>}
      size="lg"
      radius="md"
      centered
      padding="xl"
      data-testid="search-terms-modal"
    >
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap">
          <TextInput
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.currentTarget.value)}
            onKeyPress={onKeyPress}
            placeholder="Add a search term..."
            radius="md"
            size="lg"
            style={{ flex: 1 }}
            styles={{ input: { fontSize: 'var(--mantine-font-size-md)' } }}
            data-testid="search-term-input"
          />
          <Button
            onClick={onAddTerm}
            disabled={!searchTerm.trim()}
            color={primaryColor}
            radius="md"
            size="lg"
            data-testid="add-search-term-button"
          >
            Add
          </Button>
        </Group>

        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="sm">
          Your Search Terms
        </Text>

        <ScrollArea h={350}>
          {searchTerms.length > 0 ? (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" pr={{ base: 0, sm: 'sm' }}>
              {searchTerms.map((term, index) => (
                <UnstyledButton
                  key={term}
                  onClick={() => onDeleteTerm(term)}
                  data-testid={`delete-search-term-${index}`}
                  style={{
                    border: '1px solid #14B885',
                    borderRadius: 'var(--mantine-radius-md)',
                    padding: '12px 16px',
                    backgroundColor: '#E6F9F0',
                    transition: 'all 0.2s ease',
                    width: '100%',
                  }}
                >
                  <Flex justify="space-between" align="center" wrap="nowrap">
                    <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={500} size="md" c="dark.9" truncate>
                        {term}
                      </Text>
                    </Group>
                    <Box style={{ flexShrink: 0, marginLeft: '8px' }}>
                      <IconX size={20} color="#14B885" />
                    </Box>
                  </Flex>
                </UnstyledButton>
              ))}
            </SimpleGrid>
          ) : (
            <Text c="dimmed" ta="center" py="xl">
              No search terms added
            </Text>
          )}
        </ScrollArea>

        <Group grow gap="md" mt="md">
          <Button
            onClick={onSave}
            color={primaryColor}
            radius="md"
            size="md"
            data-testid="save-search-terms-button"
          >
            Save
          </Button>
          <Button
            onClick={onCancel}
            variant="outline"
            color={primaryColor}
            radius="md"
            size="md"
            data-testid="cancel-search-terms-button"
            styles={{
              root: {
                borderWidth: 1,
              }
            }}
          >
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
